/**
 * Validation utilities for Biblionautas sharing functionality
 * 
 * These functions extract critical security validation logic from components
 * to make it easily testable and reusable.
 */

export interface ValidationResult {
    isValid: boolean
    error?: string
}

export interface Profile {
    id: string
    email: string
    full_name?: string | null
}

/**
 * Validates that a user is not trying to share their library with themselves
 * 
 * @param emailToShare - The email address being shared with
 * @param currentUserEmail - The current user's email address
 * @returns ValidationResult with isValid=false if emails match
 */
export function validateNotSelfShare(
    emailToShare: string,
    currentUserEmail: string
): ValidationResult {
    const normalizedShareEmail = emailToShare.toLowerCase().trim()
    const normalizedUserEmail = currentUserEmail.toLowerCase().trim()

    if (normalizedShareEmail === normalizedUserEmail) {
        return {
            isValid: false,
            error: 'No puedes compartir tu biblioteca contigo mismo.'
        }
    }

    return { isValid: true }
}

/**
 * Validates that the email being shared with belongs to a registered user
 * 
 * @param emailToShare - The email address to validate
 * @param registeredProfiles - Array of all registered user profiles
 * @returns ValidationResult with isValid=false if email not found
 */
export function validateEmailExists(
    emailToShare: string,
    registeredProfiles: Profile[]
): ValidationResult {
    const normalizedEmail = emailToShare.toLowerCase().trim()

    const profileExists = registeredProfiles.some(
        profile => profile.email.toLowerCase().trim() === normalizedEmail
    )

    if (!profileExists) {
        return {
            isValid: false,
            error: 'El correo ingresado no pertenece a ningún usuario registrado en Biblionautas.'
        }
    }

    return { isValid: true }
}

/**
 * Validates email format
 * 
 * @param email - Email address to validate
 * @returns ValidationResult with isValid=false if format is invalid
 */
export function validateEmailFormat(email: string): ValidationResult {
    if (!email) return { isValid: false, error: 'El formato del correo electrónico no es válido.' }

    const normalizedEmail = email.trim()
    
    // Check basic components: one @, at least one dot, no spaces
    if (!normalizedEmail.includes('@') || normalizedEmail.includes(' ')) {
        return { isValid: false, error: 'El formato del correo electrónico no es válido.' }
    }

    const parts = normalizedEmail.split('@')
    if (parts.length !== 2) {
        return { isValid: false, error: 'El formato del correo electrónico no es válido.' }
    }

    const [user, domain] = parts
    if (!user || !domain || !domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
        return { isValid: false, error: 'El formato del correo electrónico no es válido.' }
    }

    return { isValid: true }
}

/**
 * Comprehensive validation for sharing a library
 * Combines all validation checks in the correct order
 * 
 * @param emailToShare - The email address being shared with
 * @param currentUserEmail - The current user's email
 * @param registeredProfiles - Array of registered user profiles
 * @returns ValidationResult with first error encountered, or isValid=true if all pass
 */
export function validateLibraryShare(
    emailToShare: string,
    currentUserEmail: string,
    registeredProfiles: Profile[]
): ValidationResult {
    // Check format first
    const formatCheck = validateEmailFormat(emailToShare)
    if (!formatCheck.isValid) {
        return formatCheck
    }

    // Check not sharing with self
    const selfCheck = validateNotSelfShare(emailToShare, currentUserEmail)
    if (!selfCheck.isValid) {
        return selfCheck
    }

    // Check email exists in system
    const existsCheck = validateEmailExists(emailToShare, registeredProfiles)
    if (!existsCheck.isValid) {
        return existsCheck
    }

    return { isValid: true }
}

/**
 * Finds a profile by email address
 * 
 * @param email - Email to search for
 * @param profiles - Array of profiles to search
 * @returns Profile if found, null otherwise
 */
export function findProfileByEmail(
    email: string,
    profiles: Profile[]
): Profile | null {
    const normalizedEmail = email.toLowerCase().trim()
    return profiles.find(
        p => p.email.toLowerCase().trim() === normalizedEmail
    ) || null
}
